import {
  defaultLoadableState,
  LoadableState,
  useLoadableState
} from '@carvana/merch-react-hooks';
import deepmerge from 'deepmerge';
import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import TradeInPreviewAcquisition, {
  LifeCycleState
} from '~/contracts/common/Trade';
import { DeepPartial } from '~/types';

function getActiveAcquisition(trades: TradeInPreviewAcquisition[]) {
  return trades.find(
    (a: TradeInPreviewAcquisition) =>
      a.lifeCycleState === LifeCycleState.Active ||
      a.lifeCycleState === LifeCycleState.Scheduled ||
      a.lifeCycleState === LifeCycleState.AppliedToPurchase
  );
}

export type ContextType = {
  activeTrade: TradeInPreviewAcquisition | null;
  isTradeInApplied: boolean;
  selectedTrade: TradeInPreviewAcquisition | null;
  setActiveTrade: (trade: TradeInPreviewAcquisition | null) => void;
  setSelectedTrade: (trade: TradeInPreviewAcquisition | null) => void;
  setTradeInApplied: Dispatch<SetStateAction<boolean>>;
  toggleAppliedTrade: () => void;
  trades: LoadableState<TradeInPreviewAcquisition[]>;
};

export const defaultContext: ContextType = {
  activeTrade: null,
  isTradeInApplied: true,
  selectedTrade: null,
  setActiveTrade() {},
  setSelectedTrade() {},
  setTradeInApplied() {},
  toggleAppliedTrade() {},
  trades: defaultLoadableState<TradeInPreviewAcquisition[]>([])
};

const TradeContext = createContext(defaultContext);

export type OwnProps = React.PropsWithChildren<{
  isTradeInApplied?: boolean;
}>;

export function TradeContextProvider({ ...props }: OwnProps) {
  const [activeTrade, setActiveTrade] = useState(defaultContext.activeTrade);
  const [selectedTrade, setSelectedTrade] = useState(
    defaultContext.selectedTrade
  );
  const [isTradeInApplied, setTradeInApplied] = useState(
    props.isTradeInApplied ?? true
  );
  const trades = useLoadableState<TradeInPreviewAcquisition[]>([]);

  useEffect(() => {
    setActiveTrade(getActiveAcquisition(trades.data) ?? null);
  }, [trades.data]);

  const toggleAppliedTrade = useCallback(() => {
    setTradeInApplied(v => !v);
  }, []);

  const value = useMemo<ContextType>(
    () => ({
      activeTrade,
      isTradeInApplied,
      setActiveTrade,
      setSelectedTrade,
      setTradeInApplied,
      selectedTrade,
      toggleAppliedTrade,
      trades
    }),
    [activeTrade, isTradeInApplied, selectedTrade, toggleAppliedTrade, trades]
  );

  return (
    <TradeContext.Provider value={value}>
      {props.children}
    </TradeContext.Provider>
  );
}

export const generateTradeContext = (
  customState: DeepPartial<ContextType>
): ContextType =>
  deepmerge<ContextType, DeepPartial<ContextType>>(defaultContext, customState);

export function useGetTradeInStatus(): {
  isActive: boolean;
  isExpired: boolean;
  isPending: boolean;
} {
  const { lifeCycleState } = useContext(TradeContext)?.selectedTrade ?? {};

  return useMemo(
    () => ({
      isActive:
        lifeCycleState === LifeCycleState.Active ||
        lifeCycleState === LifeCycleState.Scheduled ||
        lifeCycleState === LifeCycleState.AppliedToPurchase,
      isExpired: lifeCycleState === LifeCycleState.Expired,
      isPending:
        lifeCycleState === LifeCycleState.Manual ||
        lifeCycleState === LifeCycleState.Rejected
    }),
    [lifeCycleState]
  );
}

export function useIsTradeInApplied() {
  return useContext(TradeContext).isTradeInApplied;
}

export function useIsTradeLoaded() {
  return useContext(TradeContext).trades.isLoaded;
}

export function useSelectedTrade() {
  return useContext(TradeContext).selectedTrade;
}

export function useGetAcquisitionNameForApiRequest() {
  const {
    selectedTrade,
    trades: { data: trades },
    isTradeInApplied
  } = useContext(TradeContext);

  if (!isTradeInApplied) return '';

  return trades.length <= 1 ? '' : selectedTrade?.id ?? '';
}

TradeContextProvider.displayName = 'TradeContextProvider';

export default TradeContext;
